import { TableItem } from 'components/atoms';
import { FormInput } from 'components/molecules';
import { QueryFilter, Table, TopBar } from 'components/organisms';
import { useComponentVisible } from 'hooks';
import { useInfiniteTable } from 'hooks/useInfiniteTable';
import { useParamId } from 'hooks/useParamId';
import i18n from 'locales/i18n';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useGetStatementSummaryRowsInfinite } from 'services/statements';
import type { IStatementSummaryRowsFilterParams } from 'services/statements';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { formatCurrencyFull } from 'utils/global/currency.utils';

const SummaryRows: React.FC = () => {
  const id = useParamId('statements');
  const { t } = useTranslation();
  
  const [filters, setFilters] = useState<IStatementSummaryRowsFilterParams>({});
  const rows = useGetStatementSummaryRowsInfinite(id, filters);

  const { ref, isComponentVisible, handleComponentVisible } =
    useComponentVisible<HTMLDivElement>(false, true, 'statements.summary-rows');

  const tableData = useInfiniteTable(rows, { selectable: false });

  const onDebouncedFormChange = (values: IStatementSummaryRowsFilterParams) => {
    setFilters(values);
  };

  const onCopySmartLink = (value?: string | null | undefined) => {
    if (value) {
      navigator.clipboard.writeText(value);
      toast.success(
        t('toast-message.copied-what', {
          what: value,
        }),
        { id: value }
      );
    }
  };

  return (
    <div className="w-full h-full gap-2 flex flex-col">
      <TopBar
        title={t('statements.tabs.summaryRows')}
        hideBackButton
        topbarContent={
          <QueryFilter.ButtonOpener
            i18nextPrefix="statements"
            onClick={handleComponentVisible}
            variant={BUTTON_VARIANT.default}
            filters={filters}
            isClear
          />
        }>
        <QueryFilter<IStatementSummaryRowsFilterParams>
          i18nextPrefix="statements"
          initialValues={filters}>
          <>
            <QueryFilter.Form<IStatementSummaryRowsFilterParams>
              ref={ref}
              isOpen={isComponentVisible}
              onDebouncedFormChange={onDebouncedFormChange}>
              <QueryFilter.InputsGrid>
                <FormInput.Text
                  name="ean"
                  label={t('input.label.ean')}
                />
                <FormInput.Text
                  name="isrc"
                  label={t('input.label.isrc')}
                />
                <FormInput.Text
                  name="channelId"
                  label={t('input.label.channelId')}
                />
                <FormInput.Text
                  name="videoId"
                  label={t('input.label.videoId')}
                />
                <FormInput.Text
                  name="assetId"
                  label={t('input.label.assetId')}
                />
              </QueryFilter.InputsGrid>
            </QueryFilter.Form>
            <QueryFilter.Labels
              isFormOpen={isComponentVisible}
              customLabel={(data: any) => {
                if (data.value && data.value.trim() !== '') {
                  return `${data.value}`;
                }
                return undefined;
              }}
            />
          </>
        </QueryFilter>
      </TopBar>
      <Table
        id={'SummaryRows'}
        size='compact'
        {...tableData}
        // displayMode
        columns={[
        {
          label: t('input.label.identifier'),
          renderCell: item => (
            <TableItem>
              {!!item.ean?.length && (
                <TableItem.Text
                  text={item.ean}
                  isSupplementary
                  onClick={() => onCopySmartLink(item.ean)}
                />
              )}
              {!!item.isrc?.length && (
                <TableItem.Text
                  text={item.isrc}
                  isSupplementary
                  onClick={() => onCopySmartLink(item.isrc)}
                />
              )}
              {!!item.channelId?.length && (
                <TableItem.Text
                  text={item.channelId}
                  isSupplementary
                  onClick={() => onCopySmartLink(item.channelId)}
                />
              )}
              {!!item.videoId?.length && (
                <TableItem.Text
                  text={item.videoId}
                  isSupplementary
                  onClick={() => onCopySmartLink(item.videoId)}
                />
              )}
              {!!item.assetId?.length && (
                <TableItem.Text
                  text={item.assetId}
                  isSupplementary
                  onClick={() => onCopySmartLink(item.assetId)}
                />
              )}
            </TableItem>
          ),
          resize: true,
        },

        {
          label: t('input.label.country'),
          renderCell: item => (
            <TableItem>
              <TableItem.Text
                text={
                  i18n.language === 'en'
                    ? item.country.englishName
                    : item.country.polishName
                }
              />
              {item.countryCode && (
                <TableItem.Text text={item.countryCode} isSupplementary />
              )}
            </TableItem>
          ),
          resize: true,
        },
        {
          label: t('input.label.totalValue'),
          renderCell: item => (
            <TableItem>
              <TableItem.Text text={formatCurrencyFull(item.totalValue)} />
              <TableItem.Text
                text={`${item.quantity} x ${formatCurrencyFull(item.unitPrice)}`}
                isSupplementary
              />
            </TableItem>
          ),
        },
      ]}
    />
    </div>
  );
};

export default SummaryRows;
